import { JSX, useMemo } from 'react';
import orMiniCtaBlockB from './or-mini-cta-block-b.variants';
import classNames from 'classnames';

// components
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';

// types
import { IOrMiniCtaBlockBProps } from './or-mini-cta-block-b.types';
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';

/**
 * `OrMiniCtaBlockB` is a lightweight CTA block that displays optional text and an icon,
 * paired with a single CTA. Unlike Block A, it does **not** support modals or titles —
 * it's designed for simple, direct interactions.
 *
 * It automatically sets `interactLocation` for GTM based on the CTA's destination URL.
 *
 */
export const OrMiniCtaBlockB = ({
  children,
  theme,
  icon,
  cta,
  topSpacing = 'md',
  bottomSpacing = 'md',
  topDivider = false,
  bottomDivider = false,
}: IOrMiniCtaBlockBProps): JSX.Element => {
  const { base, contents, iconContainer, iconColor, descriptionContainer } =
    orMiniCtaBlockB({
      theme,
      topSpacing,
      bottomSpacing,
    });

  const interactionLocationNoModal = useMemo(() => {
    let bannerInteractionLocationNoModal = 'or-mini-cta-block-b';

    if (
      cta?.href?.includes('youtube') &&
      cta?.label?.toLowerCase().includes('tour')
    ) {
      bannerInteractionLocationNoModal = 'video-tour';
    }

    if (
      cta?.href?.includes('matterport') &&
      cta?.label?.toLowerCase().includes('tour')
    ) {
      bannerInteractionLocationNoModal = 'virtual-tour';
    }

    return bannerInteractionLocationNoModal;
  }, [cta?.href, cta?.label]);

  // todo: update theme to dynamic val
  return (
    <div data-theme="light">
      <aside data-testid="or-mini-cta-block-b" className="container">
        {topDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
        <div className={classNames(base())}>
          <div data-theme={theme} className={contents()}>
            {icon && (
              <span className={iconContainer()}>
                <AtIcon icon={icon} color={iconColor()} />
              </span>
            )}

            <div className="flex flex-col justify-start lg:flex-row lg:items-center lg:justify-center">
              <div className="flex flex-col gap-y-6 lg:flex-row lg:items-center lg:justify-center lg:gap-x-6">
                {children && (
                  <span className={descriptionContainer()}>{children}</span>
                )}
              </div>
              {cta && (
                <AtClickable
                  {...cta}
                  interactLocation={interactionLocationNoModal}
                  className="mt-6 h-fit w-fit text-nowrap lg:ml-16 lg:mt-0 2xl:ml-20"
                />
              )}
            </div>
          </div>
        </div>
        {bottomDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
      </aside>
    </div>
  );
};
